import Auth from '@salesforce/commerce-sdk-react/auth';
import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext';
import { getAppOrigin } from '@salesforce/pwa-kit-react-sdk/utils/url';
import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import crossFetch from 'cross-fetch';
import fetchRetry from 'fetch-retry';
import Cookies from 'js-cookie';
import type {
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';
import type { ShopperLoginTypes } from 'commerce-sdk-isomorphic';
import { getEnv, getQueryString, toRelativeUrl } from '../utils/url-utils';
import { isHydrated, isServer } from '../utils/utils';
import { useShopperContextGet } from './customers/useShopperContextGet';
import { BFFHttpError } from './model/BFFHttpError';
import type { HttpParams } from '../utils/url-utils';
import type { BFFQueryOptions } from './model/BFFQueryOptions';
import type { FetchOptions } from './model/FetchOptions';

const isTest = typeof process !== 'undefined' && process.env?.NODE_ENV === 'test';

const bffUrl = (url: string): string => {
    const { deployTarget } = getConfig();
    const isSandbox = deployTarget.startsWith('sandbox');
    const isLocal = deployTarget.startsWith('local');

    /* Relative urls aren't supported by node-fetch, which is used by @salesforce/commerce-sdk-react
     * and @salesforce/pwa-kit-react-sdk. Also, msw uses node-fetch under the hood. After trying to poly/ponyfill fetch
     * with whatwg-fetch, it didn't work.
     * Rules are:
     * Sandbox - since we can't add localhost:1337 to ssrParameters.proxyConfigs, because of a SELF-SIGNED-CERTIFICATE error, we need to use the absolute url when testing locally
     * Test - we need to use the absolute url when we run jest, jest will set the NODE_ENV to test
     * Server - we need to use the absolute url in the server environment since it uses node-fetch
     * Client - we need to use the relative url in the client environment, this enables us to take advantage of the proxy in the server environment for CORS and Storefront preview
     */
    if (isServer || isSandbox || isTest || isLocal) {
        return url;
    }
    return toRelativeUrl(url);
};

const BFF_FETCH_RETRY_CONFIG: Parameters<typeof fetchRetry>[1] = {
    retries: 3,
    retryOn: [403, 429, 502, 503, 504, 505, 506, 507, 508, 509, 510, 511],
    retryDelay: (attempt: number) => 2 ** attempt * 600, // 600, 1200, 2400
};

const fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> =>
    isHydrated() && typeof window.fetch === 'function'
        ? window.sfccSessionCreationPromise.then(() =>
              fetchRetry(window.fetch, BFF_FETCH_RETRY_CONFIG)(input, init),
          )
        : fetchRetry(crossFetch, BFF_FETCH_RETRY_CONFIG)(input, init);

const handleHttpError = async (r: Response | Error): Promise<Response> => {
    if (r instanceof Error) {
        throw r;
    }
    if (!r.ok) {
        let body: Record<string, unknown> = {};

        if (typeof r.json === 'function') {
            try {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                body = await r.json();
            } catch (e) {
                try {
                    body = { content: await r.text() };
                } catch (e2) {
                    // Ignore
                }
            }
        }

        throw new BFFHttpError(r.status, r.statusText, body.body ?? body, body.stack as string);
    }
    return r;
};

const standardizeUrl = (url: string): string => {
    let newUrl = url;

    if (!url.startsWith('/')) {
        newUrl = `/${newUrl}`;
    }

    if (url.endsWith('/')) {
        newUrl = newUrl.substring(0, newUrl.length - 1);
    }

    return newUrl;
};

const getBffCookies = (): string | null => {
    if (isServer) {
        return null;
    }
    const dwsid = Cookies.get('x-slt-dwsid');
    const usid = Cookies.get('usid_SLT');
    const guestRefreshToken = Cookies.get('cc-nx-g_SLT');
    const registeredRefreshToken = Cookies.get('cc-nx_SLT');
    const cookies = {
        ...(dwsid ? { dwsid } : {}),
        ...(usid ? { usid_SLT: usid } : {}),
        ...(guestRefreshToken ? { 'cc-nx-g_SLT': guestRefreshToken } : {}),
        ...(registeredRefreshToken ? { 'cc-nx_SLT': registeredRefreshToken } : {}),
    };
    return Object.entries(cookies)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join('; ');
};

let ssrAuth: Auth | null = null;
const getSSRAuth = (): Auth => {
    if (ssrAuth) return ssrAuth;
    const { isSLASClientPrivate } = getEnv();
    const config = getConfig();
    const commerceApiConfig = config.app.commerceAPI;
    const appOrigin = getAppOrigin();

    const { shortCode } = commerceApiConfig.parameters;
    const { clientId } = commerceApiConfig.parameters;
    const { organizationId } = commerceApiConfig.parameters;
    const siteId = config.app.defaultSite;
    const redirectURI = `${appOrigin}/callback`;
    const proxy = `${appOrigin}${commerceApiConfig.proxyPath}`;

    ssrAuth = new Auth({
        clientId,
        organizationId,
        shortCode,
        siteId,
        proxy,
        redirectURI,
        enablePWAKitPrivateClient: isSLASClientPrivate,
    });

    return ssrAuth;
};

type Resolver<T> = T | ((authData: ShopperLoginTypes.TokenResponse) => T | Promise<T>);

export type BFFMutationOptions<T, R> = Pick<
    UseMutationOptions<T, unknown, R>,
    'onSuccess' | 'onError'
> & {
    basketCustomerId?: string;
};

export async function ssrBffGet<T>(url: string, options: FetchOptions = {}): Promise<T> {
    if (!isServer) {
        throw new BFFHttpError(400, 'Trying to call a server function in the client');
    }

    const authData = await getSSRAuth().ready();

    return bffGet<T>(url, {
        ...options,
        customerId: authData.customer_id,
        accessToken: authData.access_token,
    });
}

export async function ssrBff<T>(
    method: string,
    url: string,
    body: object,
    httpParams: HttpParams = [],
): Promise<T> {
    if (!isServer) throw new BFFHttpError(400, 'Trying to call a server function in the client');
    const authData = await getSSRAuth().ready();
    return bff<T>(method, url, {
        accessToken: authData.access_token,
        body,
        httpParams,
        customerId: authData.customer_id,
    });
}

export async function ssrBffPost<T>(
    url: string,
    body: object,
    httpParams: HttpParams = [],
): Promise<T> {
    return ssrBff<T>('POST', url, body, httpParams);
}

const getBffHeaders = (options: FetchOptions): Record<string, string> => {
    const { accessToken, customerId, basketCustomerId, headers: incomingHeaders } = options;
    const { bffApiKey } = getConfig();
    const cookies = getBffCookies();
    const { shopperContext } = options;

    const headers: Record<string, string> = {
        ...incomingHeaders,
        'x-slt-is-pwa-call': 'true',
        'x-slt-from-server-side': String(typeof window === 'undefined'),
        'x-slt-user-agent': typeof window !== 'undefined' ? navigator.userAgent : requestUserAgent,
        'x-api-key': bffApiKey,
        'Content-Type': 'application/json',
    };

    if (cookies) {
        headers['x-slt-cookies'] = cookies;
    }
    if (customerId) {
        headers['x-slt-customer-id'] = customerId;
    }
    if (basketCustomerId) {
        headers['x-slt-basket-customer-id'] = basketCustomerId;
    }
    if (accessToken) {
        headers['x-slt-rewriteauth'] = `Bearer ${accessToken}`;
    }

    if (shopperContext && shopperContext.effectiveDateTime) {
        headers['x-slt-effective-date-time'] = String(shopperContext.effectiveDateTime);
    }

    return headers;
};

export async function bff<T>(method: string, url: string, options: FetchOptions = {}): Promise<T> {
    const { body, httpParams = [] } = options;
    const standardUrl = standardizeUrl(url);
    const { bffBaseUrl } = getConfig();
    const headers = getBffHeaders(options);

    const recaptchaHeaders = { 'g-recaptcha-response': '', 'kiosk-location-id': '' };
    if (
        body &&
        'g-recaptcha-response' in body &&
        typeof body['g-recaptcha-response'] === 'string'
    ) {
        recaptchaHeaders['g-recaptcha-response'] = body['g-recaptcha-response'] || '';
    }
    if (body && 'kiosk-location-id' in body && typeof body['kiosk-location-id'] === 'string') {
        recaptchaHeaders['kiosk-location-id'] = body['kiosk-location-id'] || '';
    }
    const config = {
        credentials: 'omit',
        method,
        headers: {
            ...headers,
            ...recaptchaHeaders,
        },
        body: JSON.stringify(body),
    } as Parameters<typeof fetch>[1];
    const queryString = getQueryString(httpParams);

    return fetch(bffUrl(`${bffBaseUrl}${standardUrl}?${queryString}`), config)
        .then(handleHttpError)
        .then((r) => {
            const contentType = r.headers.get('content-type');
            const contentLength = r.headers.get('content-length');
            const shouldReadJson =
                r.status !== 204 &&
                contentType?.includes('application/json') &&
                (!contentLength || (contentLength && parseInt(contentLength, 10) > 0));
            if (shouldReadJson) {
                return r.json();
            }
            return {};
        })
        .catch(handleHttpError) as Promise<T>;
}

export async function bffDelete<T>(url: string, options: FetchOptions = {}): Promise<T> {
    const { body, httpParams = [] } = options;
    const standardUrl = standardizeUrl(url);
    const { bffBaseUrl } = getConfig();
    const headers = getBffHeaders(options);

    const config = {
        credentials: 'omit',
        method: 'DELETE',
        headers,
        body: body ? JSON.stringify(body) : null,
    } as Parameters<typeof fetch>[1];

    const queryString = getQueryString(httpParams);

    return fetch(bffUrl(`${bffBaseUrl}${standardUrl}?${queryString}`), config)
        .then(handleHttpError)
        .then((response) => {
            const contentType = response.headers.get('content-type');

            if (contentType?.includes('application/json')) {
                try {
                    return response.json();
                } catch (err) {
                    // Ignore
                }
            }

            return response;
        })
        .catch(handleHttpError) as Promise<T>;
}

export async function bffGet<T>(url: string, options: FetchOptions = {}): Promise<T> {
    const { httpParams = [] } = options;
    const standardUrl = standardizeUrl(url);
    const { bffBaseUrl } = getConfig();
    const headers = getBffHeaders(options);

    const config = {
        credentials: 'omit',
        method: 'GET',
        headers,
    } as Parameters<typeof fetch>[1];
    const queryString = getQueryString(httpParams);
    return fetch(bffUrl(`${bffBaseUrl}${standardUrl}?${queryString}`), config)
        .then(handleHttpError)
        .then((r) => {
            const contentType = r.headers.get('content-type');
            if (contentType?.includes('application/json')) {
                return r.json();
            }
            return {};
        })
        .catch(handleHttpError) as Promise<T>;
}

export async function bffPost<T>(url: string, options: FetchOptions = {}): Promise<T> {
    return bff<T>('POST', url, options);
}

export async function bffPut<T>(url: string, options: FetchOptions = {}): Promise<T> {
    return bff<T>('PUT', url, options);
}

export async function bffPatch<T>(url: string, options: FetchOptions = {}): Promise<T> {
    return bff<T>('PATCH', url, options);
}

function useBffMutation<T, R>(
    bffMethod: (url: string, options: FetchOptions) => Promise<T>,
    url: Resolver<string | null>,
    httpParams: HttpParams = [],
    keysToInvalidate: string[] = [],
    options?: BFFMutationOptions<T, R>,
): UseMutationResult<T, unknown, R> {
    const queryClient = useQueryClient();
    const authContext = useAuthContext();
    const shopperContext = useShopperContextGet();

    return useMutation<T, unknown, R>({
        mutationFn: async (body: R, params: HttpParams = []) => {
            if (!isServer) await window.sfccSessionCreationPromise;
            const authData = await authContext.ready();
            const fn = typeof url === 'function' ? url : () => url;
            const theUrl = await fn(authData);
            if (!theUrl) {
                throw new Error('the generated url is empty');
            }
            return bffMethod(theUrl, {
                accessToken: authData.access_token,
                body: body as object,
                httpParams: httpParams.concat(params),
                customerId: authData.customer_id,
                basketCustomerId: options?.basketCustomerId,
                shopperContext,
            });
        },
        onSuccess: async (data: T, variables: R, context: unknown): Promise<void> => {
            if (options?.onSuccess) {
                await options.onSuccess(data, variables, context);
            }

            if (keysToInvalidate.length) {
                const promises = keysToInvalidate.map((key) =>
                    queryClient.invalidateQueries([key], {
                        exact: false,
                    }),
                );

                await Promise.all(promises);
            }
        },
    });
}

export function useBffPostMutation<T, R>(
    url: Resolver<string | null>,
    httpParams: HttpParams = [],
    keysToInvalidate: string[] = [],
    options?: BFFMutationOptions<T, R>,
): UseMutationResult<T, unknown, R> {
    return useBffMutation<T, R>(bffPost, url, httpParams, keysToInvalidate, options);
}

export function useBffPutMutation<T, R>(
    url: Resolver<string | null>,
    httpParams: HttpParams = [],
    keysToInvalidate: string[] = [],
    options?: BFFMutationOptions<T, R>,
): UseMutationResult<T, unknown, R> {
    return useBffMutation<T, R>(bffPut, url, httpParams, keysToInvalidate, options);
}

export function useBffPatchMutation<T, R>(
    url: Resolver<string | null>,
    httpParams: HttpParams = [],
    keysToInvalidate: string[] = [],
    options?: BFFMutationOptions<T, R>,
): UseMutationResult<T, unknown, R> {
    return useBffMutation<T, R>(bffPatch, url, httpParams, keysToInvalidate, options);
}

export function useBffGet<T, V = T | undefined>(
    url: Resolver<string | null>,
    httpParams: Resolver<HttpParams> = [],
    keys: string[] = [],
    options?: BFFQueryOptions<T, V>,
): UseQueryResult<T, BFFHttpError> {
    const authContext = useAuthContext();
    const shopperContext = useShopperContextGet();
    const queryKey = keys.length
        ? keys
        : ['BFF', httpParams.length ? JSON.stringify(httpParams) : url];

    // eslint-disable-next-line @tanstack/query/prefer-query-object-syntax
    return useQuery({
        queryFn: async () => {
            if (!isServer) await window.sfccSessionCreationPromise;
            const authData = await authContext.ready();
            const fn = typeof url === 'function' ? url : (): string | null => url;
            const theUrl = await fn(authData);
            if (!theUrl) return null;
            const httpParamsFn =
                typeof httpParams === 'function' ? httpParams : (): HttpParams => httpParams;
            const theHttpParamsFn = await httpParamsFn(authData);

            return bffGet<T>(theUrl, {
                httpParams: theHttpParamsFn,
                accessToken: authData.access_token,
                headers: options?.headers,
                customerId: authData.customer_id,
                basketCustomerId: options?.basketCustomerId,
                shopperContext,
            });
        },
        queryKey,
        select: options?.select,
        initialData: options?.initialData,
        keepPreviousData: options?.keepPreviousData,
        retryOnMount: options?.retryOnMount,
        enabled: options?.enabled ?? true,
        staleTime: options?.staleTime,
        cacheTime: options?.cacheTime,
        retry: options?.retry ?? false,
        refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    } as UseQueryOptions<T, BFFHttpError>);
}
